import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatService from '../services/ChatService';

// Default facility type for the auto-run -- owner decision 2026-10-14: new
// signups no longer get asked to pick a type, every new tenant gets a
// Distribution facility built automatically. Matches one of
// warehouse_facility_type_options.flex's own {value,label} pairs exactly.
const AUTO_FACILITY_TYPE = 'Distribution';

/**
 * Blocking, non-dismissable modal shown on first login (before the main app
 * shell mounts) instead of the old chat-bubble nudge (INTAKE_GREETING) that
 * used to ask the user to type "get started". Not dismissable except by
 * completing the wizard -- but a "Log Out" action is offered throughout
 * (owner's request, 2026-09-30) so a user who landed on the wrong
 * account/tenant isn't stuck here with no way out. `onLogout` is optional --
 * callers that don't pass it just don't get the button.
 *
 * Owner decision 2026-10-14: no more facility-type picker here -- the old
 * CHOICE-step dropdown (Retail/Distribution/3PL/Parcel-DTC) is skipped
 * entirely and this modal auto-submits facility_setup_wizard with
 * facility_type=AUTO_FACILITY_TYPE immediately, going straight from
 * 'loading' to 'submitting' to 'success' with no user decision in between.
 * Still reuses the exact same backend mechanism the conversational
 * facility_setup_wizard always has -- started here via llmMode=DIRECT_TOOL
 * (skips NLU/trigger matching, which is unreliable for a hard requirement --
 * see add-wizard-tool skill's §7 reserved-prefix trigger bug) instead of a
 * typed chat message, and the submission goes through the same /chat/wizard
 * endpoint (WizardExecutor's normal chain: facility_setup_choose_type ->
 * facility_setup_execute / FACILITY_SETUP_CLONE_TEMPLATE.flex, which runs
 * synchronously in one round trip). The description field (optional, used to
 * shape sample items/customers) is simply never filled in by this auto path
 * -- build_sample_facility already defaults it when skipped.
 *
 * The success text shown here comes straight from
 * WizardExecutor.buildFacilitySetupSuccessMessage (backend), which lists the
 * real counts from build_sample_facility's own result (areas, locations,
 * items, receipts, orders, etc.) and a short "what's next" pointer --
 * response.response is a plain chat string, not a separate field.
 */
const FirstLoginFacilitySetupModal = ({ open, userId, connectedSystemId, onComplete, onLogout }) => {
  const startingWizard = useRef(false);
  const [phase, setPhase] = useState('loading'); // loading | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [successText, setSuccessText] = useState('');

  // Submits the wizard with the fixed AUTO_FACILITY_TYPE -- no dropdown, no
  // decision for the user. Pulled out of startWizard so startWizard's own
  // catch block (session-start failures) and this one (submission failures)
  // both land on the same 'error' phase/Try Again button without duplicating
  // that handling.
  const runAutoSubmit = useCallback(async (sessionId) => {
    setPhase('submitting');
    try {
      const response = await ChatService.submitWizardForm({
        userId,
        sessionId,
        connectedSystemId,
        action: 'submit',
        fieldValues: { facility_type: AUTO_FACILITY_TYPE },
      });

      if (response?.wizard?.status === 'COMPLETED') {
        setSuccessText(response?.response || 'Your warehouse is ready!');
        setPhase('success');
        return;
      }

      // Anything else is a failure (WizardExecutor sets status="ERROR" and a
      // validation.errors[0].message on the clone step failing) -- restart
      // the whole flow from scratch on retry rather than trying to resume
      // mid-chain, since the execution step carries no re-editable field.
      throw new Error(
        response?.wizard?.validation?.errors?.[0]?.message ||
        response?.response ||
        'Facility setup failed. Please try again.'
      );
    } catch (err) {
      console.error('Failed to submit facility_setup_wizard:', err);
      setErrorMessage(err.message || 'Facility setup failed. Please try again.');
      setPhase('error');
    }
  }, [userId, connectedSystemId]);

  const startWizard = useCallback(async () => {
    if (startingWizard.current) return;
    startingWizard.current = true;
    setPhase('loading');
    setErrorMessage('');
    try {
      const response = await ChatService.sendMessage(
        'Set up my facility',
        userId,
        null,
        connectedSystemId,
        { llmMode: 'DIRECT_TOOL', toolKey: 'facility_setup_wizard' }
      );

      if (response?.wizard?.status === 'COMPLETED') {
        // Already completed (e.g. a race with the gate check) -- nothing left to do.
        setSuccessText(response?.response || 'Your warehouse is already set up.');
        setPhase('success');
        return;
      }

      const wizardForm = response?.wizard?.form;
      if (!response?.sessionId || !wizardForm?.fields?.length) {
        throw new Error(response?.response || 'Could not start facility setup.');
      }

      // No form phase -- go straight into the auto-submit instead of waiting
      // for a user-picked facility type.
      await runAutoSubmit(response.sessionId);
    } catch (err) {
      console.error('Failed to start facility_setup_wizard:', err);
      setErrorMessage(err.message || 'Could not start facility setup. Please try again.');
      setPhase('error');
    } finally {
      startingWizard.current = false;
    }
  }, [userId, connectedSystemId, runAutoSubmit]);

  useEffect(() => {
    if (open) {
      startWizard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      fullScreen
      PaperProps={{ sx: { borderRadius: 0 } }}
    >
      {/* Full-viewport per explicit product direction -- the first thing a
          brand-new user sees shouldn't be a small centered card on an
          otherwise empty page. Content itself stays a comfortably-readable
          centered column (not stretched edge-to-edge), matching how a
          full-page onboarding step is normally laid out. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.5rem', textAlign: 'center' }}>
            Welcome to Foysonis Supply Chain Applications
          </DialogTitle>
          <DialogContent>
            {phase === 'loading' && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2">Setting things up...</Typography>
              </Box>
            )}

            {phase === 'submitting' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                <CircularProgress size={36} sx={{ color: '#22c55e' }} />
                <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 500 }}>
                  Building your distribution warehouse...
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  We're creating a full set of sample areas, locations, items, inventory, inbound receipts, and an order so you have real data to explore right away -- it can take a little while. Please don't close this window.
                </Typography>
              </Box>
            )}

            {phase === 'success' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 1.5 }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: '#22c55e' }} />
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                  {successText}
                </Typography>
                {/* FH-424 (owner, 2026-10-31): the sample warehouse is the place to learn -- offer the exercises */}
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 1 }}>
                  New to the system? Learn it by doing real tasks in this sample warehouse: Atom shows you each step
                  (receiving, moving stock, shipping an order and more) and checks your work.
                </Typography>
              </Box>
            )}

            {phase === 'error' && (
              <Box sx={{ py: 1 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{ px: 3, pb: 3, justifyContent: ((phase === 'loading' || phase === 'submitting' || phase === 'error') && onLogout) ? 'space-between' : 'center', gap: 1 }}
          >
            {/* Log Out stays available through loading/submitting/error -- there's
                no facility-type decision left to make it conditional on, but a
                user who landed on the wrong account still needs a way out while
                the build is running or has failed, not just once it succeeds. */}
            {(phase === 'loading' || phase === 'submitting' || phase === 'error') && onLogout && (
              <Button variant="text" color="inherit" onClick={onLogout}>
                Log Out
              </Button>
            )}
            {phase === 'error' && (
              <Button variant="contained" onClick={startWizard}>
                Try Again
              </Button>
            )}
            {phase === 'success' && (
              <Button
                variant="outlined"
                onClick={() => {
                  if (onComplete) onComplete();
                  // Atom lists the learning exercises, each with its Start button
                  setTimeout(() => window.dispatchEvent(new CustomEvent('atom:ask', {
                    detail: { sentence: 'Which learning exercises can I do?' },
                  })), 600);
                }}
                sx={{ color: '#16a34a', borderColor: '#22c55e' }}
              >
                Start Learning
              </Button>
            )}
            {phase === 'success' && (
              <Button
                variant="contained"
                onClick={() => onComplete && onComplete()}
                sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
              >
                Continue
              </Button>
            )}
          </DialogActions>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FirstLoginFacilitySetupModal;
